import React, { useEffect, useRef, useState } from "react";
import { Input } from "components/input/input.tsx";
import { Edit } from "assets/icons";
import { Resource, ResourceType } from "shared/types/resource";
import { useResources } from "shared/contexts/resources.context";
import {
  Button,
  Spinner,
} from "@heroui/react";
import { useNavigate } from "react-router-dom";
import { debounce } from "lodash";
import ButtonNew from "components/buttonNew/buttonNew";
import {
  Buildings,
  MagnifyingGlass,
  User,
  UsersThree,
} from "@phosphor-icons/react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { useSession } from "@clerk/clerk-react";
import { API_URL } from "constants/index";
import { AddressPicker } from "components/index";

export enum SearchMode {
  SEARCH_AND_REDIRECT, // when this mode is selected it displays the search bar by default (+ redirects when clicking depending on the logic passed in by onSelect)
  SEARCH_AND_PRESENT, // when this mode is selected it display the search bar only when in editMode and a clickable link otherwise to the default selected resource
  JUST_PRESENT, // just presents the resource and linking
}

interface SearchComponentProps {
  resourceFilter?: ResourceType[];
  onSelect?: (
    resource:
      | {
          id: number;
          resourceId: string | any;
          type: ResourceType;
          name: string;
        }
      | any,
  ) => void;
  label?: string;
  isCleared?: () => void;
  initialValue?: string | number;
  initialValueType?: string;
  metadataFilter?: string;
  initialResource?: Partial<Resource> | null;
  type?: "regular" | "mobile";
  api?: any;
  mode?: SearchMode;
  hasEditToggle?: boolean;
  editMode?: boolean;
  onMobileNavSelect?: any;
  customData?: any[] | undefined;
  customEndpoint?: string;
  customDataTransform?: (data: any) => any;
  classNames?: string;
}

type QuickCreateFormData = {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  companyName?: string;
  address?: {
    formattedAddress: string;
    placeId: string;
  };
};

type QuickCreateMode = 'none' | 'contact' | 'company';

const SearchComponent: React.FC<SearchComponentProps> = ({
  label,
  onSelect,
  resourceFilter = [],
  initialValue = "",
  initialResource,
  initialValueType = "object",
  metadataFilter,
  type = "regular",
  mode = SearchMode.SEARCH_AND_REDIRECT,
  hasEditToggle = false,
  isCleared,
  editMode,
  api,
  onMobileNavSelect,
  customData,
  customEndpoint,
  customDataTransform,
  classNames = ``,
}) => {
  const [searchTerm, setSearchTerm] = useState(initialValue);
  const [searchResults, setSearchResults] = useState<Resource[]>([]);
  const [showResults, setShowResults] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(editMode);
  const { resources } = useResources();
  const navigate = useNavigate();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [selectedResource, setSelectedResource] = useState<Resource | null>(
    null,
  );
  const [searchMode, setSearchMode] = useState<SearchMode>(mode);
  const [apiData, setApiData] = useState<any[]>([]); // needs to have a name field from the API
  const { session } = useSession();
  const [quickCreateData, setQuickCreateData] = useState<QuickCreateFormData>({});
  const [quickCreateMode, setQuickCreateMode] = useState<QuickCreateMode>('none');
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    console.log("Component mounted");
    console.log(type);
    setIsEditMode(editMode);
  }, [editMode]);

  useEffect(() => {
    console.log("Mode changed:", mode);
    setSearchMode(mode);
  }, [mode]);

  useEffect(() => {
    console.log(
      resources.filter((resource) => resourceFilter.includes(resource.type)),
    );
  }, [resources]);

  useEffect(() => {
    if (api) {
      const fetchData = async () => {
        try {
          setIsLoading(true);
          const response = await fetchWithAuth(api, { method: "GET" }, session);
          const data = await response.json();
          console.log("API data fetched:", data);
          setApiData(data);
          setIsLoading(false);

          // Automatically select the only result if there is exactly one
          if (data.length === 1) {
            handleSelect(data[0]);
          }
        } catch (error) {
          console.error("Error fetching data:", error);
          setIsLoading(false);
        }
      };

      fetchData();
    }
  }, [api, session]);

  useEffect(() => {
    if (initialValue && initialValueType !== "id" && resources) {
      const foundResource = resources.find(
        (resource) => resource.name === initialValue,
      );
      console.log("Initial value found resource (by name):", foundResource);
      setSelectedResource(foundResource || null);
      setSearchTerm(foundResource?.name || "");
    }
    if (initialValue && initialValueType === "id" && resources) {
      const foundResource = resources.find(
        (resource) => resource.id === initialValue,
      );
      console.log("Initial value found resource (by id):", foundResource);
      setSelectedResource(foundResource || null);
      setSearchTerm(foundResource?.name || "");
    }
    if (initialResource) {
      setSelectedResource(initialResource as Resource);
      setSearchTerm(initialResource.name as string);
    }
  }, [initialValue, initialValueType, resources, initialResource]);

  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const debouncedSearch = debounce(async (term: string) => {
    // Don't search if term is too short
    if (term.length < 2) {
      setSearchResults([]);
      setShowResults(false);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    const termLower = term.toLowerCase();
    let filteredResults: any[] = [];

    try {
      if (customEndpoint) {
        // Use custom endpoint for API search
        const entityTypes = resourceFilter.length > 0 ? resourceFilter.join(',') : undefined;
        const params = new URLSearchParams();
        params.append('query', term);
        if (entityTypes) params.append('entityTypes', entityTypes);
        if (metadataFilter) params.append('metadataFilter', metadataFilter);

        console.log(`🔍 SearchComponent: Calling ${API_URL}${customEndpoint}?${params.toString()}`);
        
        const response = await fetchWithAuth(
          `${API_URL}${customEndpoint}?${params.toString()}`,
          { method: 'GET' },
          session
        );

        if (response.ok) {
          const data = await response.json();
          console.log("🔍 SearchComponent: Raw backend data:", data);
          console.log("🔍 SearchComponent: Data length:", data.length);
          console.log("🔍 SearchComponent: Entity types in data:", data.map((item: any) => item.entityType));
          
          filteredResults = customDataTransform ? customDataTransform(data) : data;
          console.log("🔍 SearchComponent: After transform:", filteredResults);
        } else {
          console.error("Custom endpoint search failed:", response.statusText);
          filteredResults = [];
        }
      } else if (customData && customData.length > 0) {
        // Optimize custom data search
        filteredResults = customData.filter((item) => {
          const searchableFields = [
            item.firstName,
            item.lastName,
            item.id?.toString(),
            item.searchTerm,
            item.name,
            item.type,
            item.subType
          ];
          
          return searchableFields.some(field => 
            field && field.toLowerCase().includes(termLower)
          );
        });
      } else {
        // Optimize resource search
        const sourceData = apiData.length > 0 ? apiData : resources;
        
        filteredResults = sourceData.filter((resource: Resource) => {
          // Early return if type doesn't match
          if (resourceFilter.length > 0 && !resourceFilter.includes(resource.type)) {
            return false;
          }

          // Check name match first as it's required
          if (!resource.name.toLowerCase().includes(termLower)) {
            return false;
          }

          // Only check metadata if metadataFilter is provided
          if (metadataFilter) {
            return (
              resource.metadata1?.includes(metadataFilter) ||
              resource.metadata2?.includes(metadataFilter)
            );
          }

          return true;
        });

        // Limit results to improve performance
        filteredResults = filteredResults.slice(0, 50);
      }

      setSearchResults(filteredResults);
      setShowResults(true);
    } catch (error) {
      console.error("Search error:", error);
      setSearchResults([]);
      setShowResults(false);
    } finally {
      setIsLoading(false);
    }
  }, 300); // Reduced from 500ms to 300ms

  const handleSearch = (term: string) => {
    if(term == "") {
      setSearchTerm("");
      setSearchResults([]);
      setShowResults(false);
      isCleared?.();
      return;
    }

    setSearchTerm(term);
    
    // Clear results immediately if term is empty
    if (!term.trim()) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }
    
    debouncedSearch(term);
  };

  const getIcon = (type: ResourceType) => {
    switch (type) {
      case ResourceType.BUILDING:
        return <Buildings weight="duotone" size={20} />;
      case ResourceType.CONTACT:
        return <User weight="duotone" size={20} />;
      case ResourceType.COMPANY:
        return <UsersThree weight="duotone" size={20} />;
      default:
      case ResourceType.COMPANIES:
        return <UsersThree weight="duotone" size={20} />;
    }
  };

  const groupedResults = searchResults.reduce(
    (groups, result) => {
      (groups[result.type] = groups[result.type] || []).push(result);
      return groups;
    },
    {} as Record<ResourceType, Resource[]>,
  );

  const handleNavigation = (resource: {
    id: number;
    resourceId: string;
    type: ResourceType;
    name: string;
  }) => {
    console.log("Navigating to resource:", resource);
    setSearchTerm(resource.name);
    setShowResults(false); // Hide the Listbox
    navigate(`/${resource.type}/${resource.resourceId}`);
    onMobileNavSelect?.();
  };

  const handleSelect = async (resource: any) => {
    console.log("Resource selected:", resource);
    if (resource && resource.name) {
      setSearchTerm(resource.name);
      setShowResults(false);

      let selectedResource = {
        id: resource.id,
        resourceId: resource.resourceId,
        type: resource.type,
        name: resource.name,
        metadata1: resource.metadata1,
        metadata2: resource.metadata2,
        sharePointFolderId: resource.sharePointFolderId,
        sharePointFolderUrl: resource.sharePointPicturesFolderId,
        sharePointPicturesFolderId: resource.sharePointPicturesFolderId,
        sharePointPicturesFolderUrl: resource.sharePointPicturesFolderUrl,
        // Preserve custom properties for email entity search
        entityData: resource.entityData,
        fields: resource.fields,
      };

      if (resource.type === ResourceType.CONTACT) {
        try {
          const response = await fetchWithAuth(
            `${API_URL}/contact/${resource.resourceId}`,
            { method: "GET" },
            session,
          );
          if (response.ok) {
            const contactData = await response.json();
            (selectedResource as any) = {
              ...selectedResource,
              contactDetails: contactData,
            };
          } else {
            console.error("Failed to fetch contact details");
          }
        } catch (error) {
          console.error("Error fetching contact details:", error);
        }
      }

      onSelect?.(selectedResource);
      setSelectedResource(selectedResource);
    }
  };

  const handleQuickCreate = async () => {
    try {
      setIsCreating(true);
      
      const data = {
        type: quickCreateMode === 'company' ? ResourceType.COMPANY : ResourceType.CONTACT,
        ...(quickCreateMode === 'company' ? {
          companyName: quickCreateData.companyName,
          email: quickCreateData.email,
          phone: quickCreateData.phone,
          address: quickCreateData.address
        } : {
          firstName: quickCreateData.firstName,
          lastName: quickCreateData.lastName,
          email: quickCreateData.email,
          phone: quickCreateData.phone
        })
      };

      const response = await fetchWithAuth(
        `${API_URL}/contact/quick-create`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(data)
        },
        session
      );

      if (!response.ok) {
        throw new Error('Failed to create');
      }

      const result = await response.json();
      
      // Reset form
      setQuickCreateMode('none');
      setQuickCreateData({});
      
      // Use the created entity
      handleSelect(result);
    } catch (error) {
      console.error('Error creating:', error);
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className={`relative h-fit ${classNames}`} ref={dropdownRef}>
      {searchMode === SearchMode.SEARCH_AND_PRESENT && !isEditMode ? (
        selectedResource ? (
          <div className="flex justify-between gap-2">
            <a
              href={`/${selectedResource.type}/${selectedResource.resourceId}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {selectedResource.name}
            </a>
            <div className="flex gap-6">
              {hasEditToggle ? (
                <ButtonNew
                  variant="primaryMoss"
                  onClick={() => setIsEditMode((prev) => !prev)}
                  icon={<Edit />}
                  label={"Bewerken"}
                />
              ) : null}
            </div>
          </div>
        ) : (
          <div className="flex justify-between gap-2">
            <p className="p-2">Niet ingevuld</p>
            {hasEditToggle ? (
              <div className="flex gap-6">
                <ButtonNew
                  variant="primaryMoss"
                  onClick={() => setIsEditMode((prev) => !prev)}
                  icon={<Edit />}
                  label={"Bewerken"}
                />
              </div>
            ) : null}
          </div>
        )
      ) : (
        <>
          <div className="flex gap-2">
            <Input
              startContent={
                isLoading ? (
                  <Spinner size="sm" color="warning" />
                ) : (
                  <MagnifyingGlass weight="duotone" size={18} />
                )
              }
              placeholder={
                label || "Zoek een contact, een gebouw, een project, ..."
              }
              classNames={{
                input:
                  "text-neutral-primary placeholder:text-neutral-secondary-mid",
                inputWrapper:
                  "p-2 min-h-fit h-fit rounded-full bg-white shadow-form-element data-[hover=true]:bg-white group-data-[focus=true]:bg-white data-[hover=true]:shadow-form-element-hv group-data-[focus=true]:shadow-form-element-fc",
              }}
              value={searchTerm as string}
              onChange={(e) => handleSearch(e.target.value)}
            />

            {showResults && (
              <div className="absolute left-0 top-full z-50 mt-2 max-h-[70vh] w-full overflow-y-auto rounded-lg border border-neutral-primary/20 bg-white p-2 shadow-md">
                {searchResults.length > 0 ? (
                  Object.entries(groupedResults).map(([type, results]) => (
                    <div key={type} className="mb-4">
                      <div className="mb-2 text-sm font-medium text-neutral-primary uppercase tracking-wide">
                        {type}
                      </div>
                      <div className="flex flex-col gap-1">
                        {results.map((result) => (
                          <button
                            key={result.id}
                            className="flex items-center gap-3 w-full rounded-lg p-2 text-left hover:bg-neutral-50 transition-colors"
                            onClick={() => onSelect ? handleSelect(result) : handleNavigation({
                              id: result.id as number,
                              resourceId: result.resourceId,
                              type: result.type,
                              name: result.name,
                            })}
                          >
                            <span className="text-neutral-primary">
                              {getIcon(result.type)}
                            </span>
                            <div className="flex flex-col">
                              <span className="text-sm text-neutral-primary">
                                {result.name}
                              </span>
                              {result.metadata2 && (
                                <span className="text-xs text-neutral-secondary">
                                  {result.metadata2}
                                </span>
                              )}
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="p-2">
                    {quickCreateMode === 'none' ? (
                      <div className="flex flex-col gap-2">
                        <p className="text-sm text-neutral-secondary">
                          Geen resultaten gevonden.
                        </p>
                        <div className="flex gap-2">
                          {resourceFilter.includes(ResourceType.CONTACT) && (
                            <button
                              className="flex items-center gap-2 rounded-lg bg-secondary px-3 py-2 text-sm text-white hover:bg-secondary/90 transition-colors"
                              onClick={() => {
                                setQuickCreateMode('contact');
                                setQuickCreateData({});
                              }}
                            >
                              <User className="h-4 w-4" />
                              Nieuw Contact
                            </button>
                          )}
                          {resourceFilter.includes(ResourceType.COMPANY) && (
                            <button
                              className="flex items-center gap-2 rounded-lg bg-brick-primary px-3 py-2 text-sm text-white hover:bg-brick-primary/90 transition-colors"
                              onClick={() => {
                                setQuickCreateMode('company');
                                setQuickCreateData({});
                              }}
                            >
                              <Buildings className="h-4 w-4" />
                              Nieuw Bedrijf
                            </button>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-col gap-3">
                        {quickCreateMode === 'contact' && (
                          <div 
                            className="rounded-lg bg-neutral-50 p-4"
                          >
                            <div className="mb-4 flex flex-col">
                              <h3 className="text-lg font-medium text-neutral-900">
                                Contact Gegevens
                              </h3>
                              <p className="text-sm text-neutral-600">
                                Vul minimaal de naam in
                              </p>
                            </div>

                            <div className="flex flex-col gap-3">
                              <div className="flex gap-3">
                                <div className="flex-1">
                                  <label className="mb-1 block text-sm font-medium text-neutral-700">
                                    Voornaam <span className="text-red-500">*</span>
                                  </label>
                                  <input
                                    type="text"
                                    placeholder="John"
                                    value={quickCreateData?.firstName || ''}
                                    onChange={(e) => {
                                      setQuickCreateData(prev => ({ ...prev, firstName: e.target.value }));
                                    }}   
                                    className="w-full rounded-lg border border-neutral-200 bg-white p-2 text-sm text-neutral-700 shadow-sm focus:border-primary focus:outline-hidden focus:ring-1 focus:ring-primary"
                                  />
                                </div>
                                <div className="flex-1">
                                  <label className="mb-1 block text-sm font-medium text-neutral-700">
                                    Achternaam <span className="text-red-500">*</span>
                                  </label>
                                  <input
                                    type="text"
                                    placeholder="Doe"
                                    value={quickCreateData?.lastName || ''}
                                    onChange={(e) => setQuickCreateData(prev => ({ ...prev, lastName: e.target.value }))}
                                    className="w-full rounded-lg border border-neutral-200 bg-white p-2 text-sm text-neutral-700 shadow-sm focus:border-primary focus:outline-hidden focus:ring-1 focus:ring-primary"
                                  />
                                </div>
                              </div>

                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <label className="mb-1 block text-sm font-medium text-neutral-700">
                                    Email
                                  </label>
                                  <input
                                    type="email"
                                    placeholder="john.doe@example.com"
                                    value={quickCreateData?.email || ''}
                                    onChange={(e) => setQuickCreateData(prev => ({ ...prev, email: e.target.value }))}
                                    className="w-full rounded-lg border border-neutral-200 bg-white p-2 text-sm text-neutral-700 shadow-sm focus:border-primary focus:outline-hidden focus:ring-1 focus:ring-primary"
                                  />
                                </div>
                                <div>
                                  <label className="mb-1 block text-sm font-medium text-neutral-700">
                                    Telefoon
                                  </label>
                                  <input
                                    type="tel"
                                    placeholder="+32 123 45 67 89"
                                    value={quickCreateData?.phone || ''}
                                    onChange={(e) => setQuickCreateData(prev => ({ ...prev, phone: e.target.value }))}
                                    className="w-full rounded-lg border border-neutral-200 bg-white p-2 text-sm text-neutral-700 shadow-sm focus:border-primary focus:outline-hidden focus:ring-1 focus:ring-primary"
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        {quickCreateMode === 'company' && (
                          <div className="rounded-lg bg-neutral-50 p-4">
                            <div className="mb-4 flex flex-col">
                              <h3 className="text-lg font-medium text-neutral-900">
                                Bedrijf Gegevens
                              </h3>
                              <p className="text-sm text-neutral-600">
                                Vul minimaal de bedrijfsnaam in
                              </p>
                            </div>

                            <div className="flex flex-col gap-3">
                              <div>
                                <label className="mb-1 block text-sm font-medium text-neutral-700">
                                  Bedrijfsnaam <span className="text-red-500">*</span>
                                </label>
                                <input
                                  type="text"
                                  placeholder="Acme Inc."
                                  value={quickCreateData?.companyName || ''}
                                  onChange={(e) => setQuickCreateData(prev => ({ ...prev, companyName: e.target.value }))}
                                  className="w-full rounded-lg border border-neutral-200 bg-white p-2 text-sm text-neutral-700 shadow-sm focus:border-primary focus:outline-hidden focus:ring-1 focus:ring-primary"
                                />
                              </div>

                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <label className="mb-1 block text-sm font-medium text-neutral-700">
                                    Email
                                  </label>
                                  <input
                                    type="email"
                                    placeholder="info@acme.com"
                                    value={quickCreateData?.email || ''}
                                    onChange={(e) => setQuickCreateData(prev => ({ ...prev, email: e.target.value }))}
                                    className="w-full rounded-lg border border-neutral-200 bg-white p-2 text-sm text-neutral-700 shadow-sm focus:border-primary focus:outline-hidden focus:ring-1 focus:ring-primary"
                                  />
                                </div>
                                <div>
                                  <label className="mb-1 block text-sm font-medium text-neutral-700">
                                    Telefoon
                                  </label>
                                  <input
                                    type="tel"
                                    placeholder="+32 123 45 67 89"
                                    value={quickCreateData?.phone || ''}
                                    onChange={(e) => setQuickCreateData(prev => ({ ...prev, phone: e.target.value }))}
                                    className="w-full rounded-lg border border-neutral-200 bg-white p-2 text-sm text-neutral-700 shadow-sm focus:border-primary focus:outline-hidden focus:ring-1 focus:ring-primary"
                                  />
                                </div>
                              </div>

                              <AddressPicker
                                editMode={true}
                                label="Adres"
                                getAddress={(selectedPlace) => {
                                  if (selectedPlace) {
                                    setQuickCreateData(prev => ({
                                      ...prev,
                                      address: {
                                        formattedAddress: selectedPlace.description,
                                        placeId: selectedPlace.placeId
                                      }
                                    }));
                                  }
                                }}
                              />
                            </div>
                          </div>
                        )}

                        <div className="flex items-center justify-between border-t border-neutral-200 pt-3">
                          <Button
                            size="sm"
                            variant="light"
                            onPress={() => {
                              setQuickCreateMode('none');
                            }}
                          >
                            Annuleren
                          </Button>
                          <Button
                            color="primary"
                            size="sm"
                            isDisabled={
                              (quickCreateMode === 'contact' && (!quickCreateData?.firstName || !quickCreateData?.lastName)) ||
                              (quickCreateMode === 'company' && !quickCreateData?.companyName)
                            }
                            onPress={() => {
                              handleQuickCreate();
                            }}
                            isLoading={isCreating}
                          >
                            {isCreating ? 'Bezig met aanmaken...' : 'Aanmaken'}
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default SearchComponent;
